import { AfterViewInit, Directive, ElementRef, OnDestroy, Renderer2, inject } from '@angular/core';
import { DropdownComponent } from '@michelin/theme';
import { Subscription } from 'rxjs';
import { MichelinDropdownOverlayStateService } from './michelin-dropdown-overlay-state.service';

@Directive({
  standalone: true,
  selector: 'mic-dropdown-search[micDropdownAccessibility]'
})
export class MichelinDropdownAccessibilityDirective implements AfterViewInit, OnDestroy {
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly renderer = inject(Renderer2);
  private readonly dropdown = inject<DropdownComponent<object>>(DropdownComponent, { host: true });
  private readonly overlayState = inject(MichelinDropdownOverlayStateService);
  private readonly subscriptions = new Subscription();
  private removeSearchKeydownListener?: () => void;
  private bindSearchKeydownTimer?: ReturnType<typeof setTimeout>;
  private highlightOptionTimer?: ReturnType<typeof setTimeout>;
  private scrollOptionTimer?: ReturnType<typeof setTimeout>;
  private isTrackedAsOpen = false;

  ngAfterViewInit(): void {
    const projectedContentTrigger = this.elementRef.nativeElement.querySelector<HTMLElement>(
      '.grid.grid-cols-1.grid-rows-1 > [role="button"][tabindex="0"]'
    );

    if (projectedContentTrigger) {
      this.renderer.setAttribute(projectedContentTrigger, 'tabindex', '-1');
    }

    this.subscriptions.add(
      this.dropdown.openedChange.subscribe((opened) => {
        this.setTrackedAsOpen(opened);

        if (opened) {
          this.bindSearchKeydownTimer = setTimeout(() => this.bindSearchKeydownListener());
        } else {
          this.removeSearchKeydownListener?.();
          this.removeSearchKeydownListener = undefined;
          this.restoreFocusIfSearchInputHadIt();
        }
      })
    );
    this.subscriptions.add(
      this.dropdown.search.valueChanges.subscribe(() => {
        clearTimeout(this.highlightOptionTimer);
        this.highlightOptionTimer = setTimeout(() => this.highlightSoleEnabledOption());
      })
    );
  }

  ngOnDestroy(): void {
    this.removeSearchKeydownListener?.();
    clearTimeout(this.bindSearchKeydownTimer);
    clearTimeout(this.highlightOptionTimer);
    clearTimeout(this.scrollOptionTimer);
    this.setTrackedAsOpen(false);
    this.subscriptions.unsubscribe();
  }

  private handleSearchKeydown(event: KeyboardEvent): void {
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
      return;
    }

    const enabledOptions = this.dropdown.options?.filter((option) => !option.disabled) ?? [];
    if (enabledOptions.length !== 1 || !['ArrowDown', 'ArrowUp', 'Enter'].includes(event.key)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const option = enabledOptions[0];
    option.setActiveStyles();

    if (event.key === 'Enter') {
      const enabledOptionElements = this.getOpenPanel()?.querySelectorAll<HTMLElement>('mat-option:not([aria-disabled="true"])');
      enabledOptionElements?.item(0).click();
    }
  }

  private bindSearchKeydownListener(): void {
    this.removeSearchKeydownListener?.();

    const panel = this.getOpenPanel();
    if (!panel) {
      return;
    }

    const listener = (event: KeyboardEvent): void => {
      if (event.target instanceof HTMLInputElement && event.target.type === 'text') {
        this.handleSearchKeydown(event);
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          clearTimeout(this.scrollOptionTimer);
          this.scrollOptionTimer = setTimeout(() => this.scrollActiveOptionIntoView());
        }
      }
    };

    panel.addEventListener('keydown', listener, true);
    this.removeSearchKeydownListener = () => panel.removeEventListener('keydown', listener, true);
  }

  private getOpenPanel(): HTMLElement | null {
    const select = this.elementRef.nativeElement.querySelector<HTMLElement>('mat-select');
    const panelId = select?.getAttribute('aria-controls');
    return panelId ? this.elementRef.nativeElement.ownerDocument.getElementById(panelId) : null;
  }

  /**
   * Michelin explicitly moves real DOM focus into the search input rendered inside the CDK
   * overlay panel (see its `setFocusOnSearchInput()`), which lives outside this component's — and
   * any ancestor modal's — DOM subtree. Once the panel is later removed from the DOM, the browser
   * has nowhere to fall back to and defaults focus to `<body>`, escaping any ancestor focus trap
   * (e.g. the work-order line modal), which then lets Tab jump to the page's global skip-nav link.
   * Returning focus to the `mat-select` trigger — the same element vanilla Material keeps focused
   * throughout — keeps focus inside the modal regardless of why/how the panel closed.
   */
  private restoreFocusIfSearchInputHadIt(): void {
    const searchInputElement = this.dropdown.searchInput?.nativeElement as HTMLElement | undefined;
    const activeElement = this.elementRef.nativeElement.ownerDocument.activeElement;

    if (searchInputElement && activeElement === searchInputElement) {
      this.elementRef.nativeElement.querySelector<HTMLElement>('mat-select')?.focus();
    }
  }

  private highlightSoleEnabledOption(): void {
    const enabledOptions = this.dropdown.options?.filter((option) => !option.disabled) ?? [];
    if (enabledOptions.length === 1) {
      enabledOptions[0].setActiveStyles();
    }
  }

  private scrollActiveOptionIntoView(): void {
    this.getOpenPanel()
      ?.querySelector<HTMLElement>('.mat-mdc-option-active:not([aria-disabled="true"])')
      ?.scrollIntoView({ block: 'nearest' });
  }

  private setTrackedAsOpen(opened: boolean): void {
    if (opened === this.isTrackedAsOpen) {
      return;
    }

    this.isTrackedAsOpen = opened;
    if (opened) {
      this.overlayState.markOpened();
    } else {
      this.overlayState.markClosed();
    }
  }
}
